/**
 * Shared legal-document manifest and consent contract.
 *
 * One source of truth used by:
 *  - storefront legal page content + consent gate,
 *  - api `/v1/storefront/me/legal-consent` controller + service,
 *  - db migration for `user_legal_consents` (document-key allow list).
 *
 * Bumping a version id in `LEGAL_CURRENT_VERSIONS` invalidates the
 * matching accepted-version row on the next consent check, which is the
 * mechanism the PRD requires for partial-staleness re-consent.
 */
import { z } from "zod";
export const LEGAL_DOCUMENT_IDS = [
    "terms",
    "privacy",
    "cookie_policy",
    "delivery_policy",
];
export const LEGAL_CONSENT_DOCUMENT_IDS = [
    "terms",
    "privacy",
    "cookie_policy",
];
/**
 * Currently published version id per legal document. Bump the relevant
 * string whenever the corresponding runtime page content changes
 * materially. Version ids are intentionally explicit so that content +
 * version always ship together in the same change set.
 */
export const LEGAL_CURRENT_VERSIONS = {
    terms: "2026-05-16.1",
    privacy: "2026-05-16.1",
    cookie_policy: "2026-05-16.1",
    delivery_policy: "2026-05-16.1",
};
export const LEGAL_CONSENT_SOURCES = [
    "continued_use",
    "guest_reconciliation",
];
/**
 * PUT /v1/storefront/me/legal-consent request body. The client never
 * declares which versions are current — the server derives that from
 * `LEGAL_CURRENT_VERSIONS` at write time.
 */
export const RecordLegalConsentRequestSchema = z.object({
    source: z.enum(LEGAL_CONSENT_SOURCES),
});
/**
 * Pure helper — returns the subset of consent-governed documents whose
 * accepted version does not match the current version map. Missing
 * documents count as stale.
 */
export function deriveStaleDocuments(current, accepted) {
    const stale = [];
    for (const id of LEGAL_CONSENT_DOCUMENT_IDS) {
        if (accepted[id] !== current[id])
            stale.push(id);
    }
    return stale;
}
